import type {
  CallbackFlowOutcome,
  CallbackNotice,
  ShopifyCompletionCommand,
  ShopifyCompletionDto,
  ShopifyStoreReturn,
} from '../models/callback.types'
import { CONNECTIONS_ROUTE } from '../routeTarget'
import type { RefreshOutcome } from '../service/integrationService'

import type { AccountContextReloadOutcome } from '@/features/account'

export interface ShopifyInstallFlowDependencies {
  complete: (command: ShopifyCompletionCommand) => Promise<ShopifyCompletionDto>
  installStore: (command: ShopifyStoreReturn) => Promise<void>
  reloadAccount: () => Promise<AccountContextReloadOutcome>
  refreshIntegrations: () => Promise<RefreshOutcome>
  notify: (notice: CallbackNotice) => void
}

export class ShopifyInstallFlow {
  constructor(private readonly deps: ShopifyInstallFlowDependencies) {}

  async complete(command: ShopifyCompletionCommand): Promise<CallbackFlowOutcome> {
    try {
      await this.deps.complete(command)
      const account = await this.deps.reloadAccount()
      if (account.status === 'failed') throw account.error

      const integrations = await this.deps.refreshIntegrations()
      if (!integrations.ok) throw new Error('Integration context refresh failed')

      this.deps.notify({ tone: 'success', key: 'integrations.shopify.installSuccess' })
      return { destination: CONNECTIONS_ROUTE }
    } catch (error) {
      this.deps.notify({ tone: 'error', key: 'integrations.shopify.installFailed' })
      throw error
    }
  }

  execute(command: ShopifyCompletionCommand): Promise<CallbackFlowOutcome> {
    return this.complete(command)
  }

  /**
   * The OAuth return's other half (ORP-20383). `complete()` covers the `shopify_context` contract;
   * this covers what the backend ACTUALLY redirects with today — `shopify_success=1&shop_id=`,
   * a store it has already bound but not yet registered as an integration.
   *
   * Deliberately no `destination`: the caller still owns where the return lands (the onboarding
   * decision in `queryCallbacks`), and this only restores the registration + ingestion side
   * effect. It throws after notifying so a failure cannot pass for a completed install.
   */
  async installStore(command: ShopifyStoreReturn): Promise<void> {
    try {
      await this.deps.installStore(command)
      const integrations = await this.deps.refreshIntegrations()
      if (!integrations.ok) throw new Error('Integration context refresh failed')
    } catch (error) {
      this.deps.notify({ tone: 'error', key: 'integrations.shopify.installFailed' })
      throw error
    }
  }
}
